// filepath: apps/web/src/features/admin/hmacKeys/queries.ts
//
// FR-UI-ADM-7 -- TanStack Query hooks for the HMAC key rotation action.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1 / FR-UI-CORE-1 pattern). Switching tenants does
// NOT leak the prior tenant's key list -- the cache key changes,
// the query refetches.
//
// Mutations carry `idempotency_key: crypto.randomUUID()` (INV-3 /
// FR-UI-PROM-2 pattern) so retries are idempotent. The destructive
// rotate (when a grace-period key is already present) is gated by
// a separate confirmation dialog, not by the query layer.
//
// The new key value returned by POST /rotate is NOT stored in
// TanStack Query cache -- it is held in local React state only
// (managed by the OneTimeCopyDialog component -- sessionStorage
// cleared on unmount).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  HmacKeyFetchError,
  IssueKeyResponse,
  RotateKeyRequest,
  RotateKeyResponse,
  WebhookKeyMeta,
} from "./types";

/**
 * Cache keys for the HMAC key rotation action. tenantSlug is
 * the 2nd segment (INV-1). Mirrors the FR-UI-ADM-1 adminKeys
 * shape; co-located here rather than in the admin/queries.ts
 * barrel so the hmacKeys surface is self-contained.
 */
export const webhookKeyKeys = {
  keys: (tenantSlug: string) => ["admin", tenantSlug, "webhook-keys"] as const,
};

// ---- Hooks ----

/**
 * useWebhookKeys -- GET /api/webhooks/keys
 *
 * Returns the typed union `HmacKeyFetchError` on failure; the
 * page maps each kind to its dedicated error component.
 */
export function useWebhookKeys() {
  const { tenantSlug } = useTenant();
  return useQuery<WebhookKeyMeta[], ApiError>({
    queryKey: webhookKeyKeys.keys(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/webhooks/keys");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as WebhookKeyMeta[];
    },
  });
}

/**
 * useRotateKey -- POST /api/webhooks/keys/rotate.
 *
 * Sends `X-Idempotency-Key: crypto.randomUUID()`. On 200 returns
 * the RotateKeyResponse (which carries `new_key_value` ONCE --
 * INV-5). The caller (RotateKeyDialog) is responsible for rendering
 * the new key value in a one-time copy dialog and clearing it on
 * unmount.
 *
 * On 400 with `invalid_grace_window`, throws a typed
 * HmacKeyFetchError so the dialog can highlight the slider.
 *
 * On 403 with `staging_only_forbidden`, throws a typed
 * HmacKeyFetchError so the page can render the FR-UI-PROM-1
 * staging-only message.
 */
export function useRotateKey() {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    RotateKeyResponse,
    Error & { code?: string },
    RotateKeyRequest
  >({
    mutationFn: async (body) => {
      const r = await apiFetch("/api/webhooks/keys/rotate", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({ error: r.statusText }));
        const code =
          r.status === 400 && errBody.error === "invalid_grace_window"
            ? "invalid_grace_window"
            : r.status === 403
              ? "staging_only_forbidden"
              : `http_${r.status}`;
        const err = new Error(errBody.error || r.statusText) as Error & {
          code?: string;
        };
        err.code = code;
        throw err;
      }
      return (await r.json()) as RotateKeyResponse;
    },
    onSuccess: () => {
      // Invalidate the keys list so the new active + grace-period rows appear.
      qc.invalidateQueries({ queryKey: webhookKeyKeys.keys(tenantSlug) });
      // BA contract (uat-ba note_13124): disarm after the mutation
      // completes so a subsequent unrelated 401 does not falsely
      // re-issue this mutation.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useIssueKey -- POST /api/webhooks/keys.
 *
 * Sends `X-Idempotency-Key: crypto.randomUUID()`. On 201 returns
 * the IssueKeyResponse (which carries `key_value` ONCE -- INV-5).
 * The caller (IssueKeyFab) is responsible for rendering the new
 * key value in a one-time copy dialog and clearing it on unmount.
 */
export function useIssueKey() {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<IssueKeyResponse, Error & { code?: string }, void>({
    mutationFn: async () => {
      const r = await apiFetch("/api/webhooks/keys", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
        idempotencyKey: true,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({ error: r.statusText }));
        const code =
          r.status === 403 ? "staging_only_forbidden" : `http_${r.status}`;
        const err = new Error(errBody.error || r.statusText) as Error & {
          code?: string;
        };
        err.code = code;
        throw err;
      }
      return (await r.json()) as IssueKeyResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: webhookKeyKeys.keys(tenantSlug) });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useRevokeKey -- DELETE /api/webhooks/keys/{key_id}.
 *
 * Sends `X-Idempotency-Key: crypto.randomUUID()`. On 204 the row
 * is optimistically removed from the cache via `setQueryData`. On
 * 404 the row is treated as already-revoked (no-op).
 */
export function useRevokeKey() {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<void, Error & { code?: string }, string>({
    mutationFn: async (keyId) => {
      const r = await apiFetch(
        `/api/webhooks/keys/${encodeURIComponent(keyId)}`,
        {
          method: "DELETE",
          idempotencyKey: true,
        },
      );
      if (!r.ok && r.status !== 404) {
        const errBody = await r.json().catch(() => ({ error: r.statusText }));
        const code =
          r.status === 403 ? "staging_only_forbidden" : `http_${r.status}`;
        const err = new Error(errBody.error || r.statusText) as Error & {
          code?: string;
        };
        err.code = code;
        throw err;
      }
    },
    onSuccess: (_data, keyId) => {
      // Optimistically remove the revoked row from the cache.
      qc.setQueryData<WebhookKeyMeta[]>(
        webhookKeyKeys.keys(tenantSlug),
        (prev) => (prev ?? []).filter((k) => k.key_id !== keyId),
      );
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * mapApiError -- maps a thrown ApiError to a typed HmacKeyFetchError.
 *
 * Used by HmacKeysPage to pick the right error component.
 */
export function mapApiError(err: unknown): HmacKeyFetchError {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code: string }).code;
    const message = (err as { message?: string }).message ?? "Unknown error";
    const status =
      (err as { status?: number }).status ??
      (code === "invalid_grace_window"
        ? 400
        : code === "staging_only_forbidden"
          ? 403
          : 500);
    return {
      kind:
        code === "invalid_grace_window"
          ? "invalid_grace_window"
          : code === "staging_only_forbidden"
            ? "staging_only_forbidden"
            : status === 403
              ? "error_permission_denied"
              : "error_fetch",
      status,
      message,
    };
  }
  if (err instanceof ApiError) {
    return {
      kind: err.status === 403 ? "error_permission_denied" : "error_fetch",
      status: err.status,
      message: err.message,
    };
  }
  return { kind: "error_fetch", status: 500, message: String(err) };
}
